import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import * as authApi from '../api/authApi';
import { STORAGE_KEYS } from '../utils/storageKeys';

const AuthContext = createContext(null);

function readStoredUser() {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.USER);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function pickToken(data) {
  if (!data || typeof data !== 'object') return null;
  return data.token ?? data.accessToken ?? data.access_token ?? null;
}

function pickUser(data) {
  if (!data || typeof data !== 'object') return null;
  return data.user ?? data.User ?? null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readStoredUser());
  const [isAuthenticated, setIsAuthenticated] = useState(() =>
    Boolean(localStorage.getItem(STORAGE_KEYS.TOKEN))
  );

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const login = useCallback(
    async (credentials) => {
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);

      const data = await authApi.login(credentials);
      const token = pickToken(data);
      if (!token) {
        throw new Error('Invalid login response: missing token.');
      }

      const nextUser = pickUser(data);
      localStorage.setItem(STORAGE_KEYS.TOKEN, token);
      if (nextUser) {
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(nextUser));
      }
      setUser(nextUser);
      setIsAuthenticated(true);

      return { token, user: nextUser };
    },
    []
  );

  const value = useMemo(
    () => ({
      user,
      setUser,
      login,
      logout,
      isAuthenticated,
    }),
    [user, login, logout, isAuthenticated]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
